import { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import { CaretDown } from "@phosphor-icons/react";
import RoundIcon from "./RoundIcon";
import { useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../../config/paths";
import { setSelectedRound } from "../../../../context/Store/slices/issbRoundSlice";
import { issbKeys, useFoundationV2DatapointUsage, useGetQuestionnaireRounds } from "../../../../hooks/query/issb";
import { constructQueryParamsString, getFormattedNumValue, showErrorToast } from "../../../../utils";
import { isFoundationLikeRound } from "../../../../utils/roundFilters";
import { formatPeriod } from "../../../../utils/financialYear";
import { metadataEntries, sourceLabel } from "./EditableMetricRow";
import { figureHistory } from "../../../../utils/foundationReviewCompare";
import { QCardBody } from "../../IssbDataInput/components/QuestionCard";
import questionService from "../../../../api/issb/question.service";

const CONFIDENCE_COLORS = { high: "green", medium: "yellow", low: "red" };

/**
 * Round status, mapped from the one place it exists today — `IssbQuestionnaire.state`
 * (DRAFT/ACTIVE/CLOSED/...), already read by `useGetQuestionnaireRounds`. "Current round" is not a
 * backend concept at all — it's the round held in Redux (the navbar's selection), checked separately below
 * rather than folded into this map.
 */
const ROUND_STATUS_TAG = {
  ACTIVE: { label: "Open", color: "#90CDF4", bg: "rgba(66,153,225,.22)" },
  CLOSED: { label: "Closed", color: "#CBD5E0", bg: "rgba(255,255,255,.08)" },
};

/**
 * The (i) icon's destination: provenance on the left ("About this figure"), the usage trace grouped by
 * round on the right ("Where it's used") — metrics-table-2a's info overlay.
 *
 * The usage endpoint (`FoundationDatapointUsageQuery`/`FoundationQuestionUsageDto`) carries only round id,
 * stage and version — no owner, no answer text, no quality tags. Expanding a requirement card therefore
 * fetches the real question (`questionService.get`, the same per-id GET `AnswerDetailPage.jsx` uses) on
 * demand, and renders it with `QCardBody` — the exact component the Requirements page's own expanded
 * `QuestionCard` uses — so this shows the actual answer (with its own clickable inline citations via
 * `RichTextViewer`), Owner/Assignees/Stage and the status pills, not just the question text. `readOnly` on
 * `QCardBody` keeps this a read-only usage view: it must not let someone change the real question's
 * assignees or status from inside a datapoint lookup. The source document's "Open" link from the original
 * design brief is still omitted: no endpoint resolves `sourceFileInfoId` to a downloadable `documentId`,
 * and there is no page-anchored viewer in the app to open it in anyway.
 */
export default function MetricInfoOverlay({ isOpen, onClose, row, dataYear = null }) {
  const { data: usage, isLoading } = useFoundationV2DatapointUsage(isOpen ? row?.datapointUid : null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useToast();
  const currentRoundId = useSelector((state) => state.issbRound.currentRoundId);
  const [openQuestionKey, setOpenQuestionKey] = useState(null);
  const [openQuestionId, setOpenQuestionId] = useState(null);

  /*
    The requirement's own answer, text and all — fetched only once a card is actually expanded, and keyed
    on the question id (unlike the dead/unused `useGetAnswerOfQuestion`, whose query key omits `id`
    entirely and would collide across the several different questions this one modal can expand). The
    usage endpoint (`FoundationQuestionUsageDto`) carries no answer content, owner, assignees or status —
    only the question text and a stage name — so this is the same per-question GET the Requirements page's
    own answer page uses (`AnswerDetailPage.jsx`), not a new endpoint.
  */
  const { data: questionData, isLoading: isLoadingQuestion } = useQuery(
    [...issbKeys.answer(), openQuestionId],
    () => questionService.get(openQuestionId),
    {
      enabled: !!openQuestionId,
      onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    }
  );

  /*
    The rounds the user can actually open, and their name/status. Needed because the answer page takes its
    round from Redux, not from the URL — a link has to SELECT the round before navigating — and because
    `FoundationQuestionUsageDto` only carries a round id, never a name. Enabled with the overlay so the
    table does not pay for it.
  */
  const { data: roundsData } = useGetQuestionnaireRounds(
    constructQueryParamsString({ page: 0, size: 500 }),
    { enabled: isOpen }
  );
  const roundsById = useMemo(
    () =>
      Object.fromEntries(
        (roundsData?.content ?? [])
          .filter((round) => !isFoundationLikeRound(round))
          .map((round) => [round.id, round])
      ),
    [roundsData]
  );

  // Wrapped in its own useMemo rather than `usage?.questions ?? []` inline — that fallback creates a new
  // array reference every render, which would make roundGroups' dependency below re-sort on every render.
  const questions = useMemo(() => usage?.questions ?? [], [usage]);
  const roundGroups = useMemo(() => {
    const byRound = new Map();
    questions.forEach((q) => {
      const key = q.roundId ?? "unknown";
      if (!byRound.has(key)) byRound.set(key, []);
      byRound.get(key).push(q);
    });
    return Array.from(byRound, ([roundId, items]) => ({ roundId, round: roundsById[roundId], items }));
  }, [questions, roundsById]);

  const openAnswer = (usageItem) => {
    const round = roundsById[usageItem.roundId];
    if (!round) return;
    dispatch(setSelectedRound(round));
    onClose?.();
    navigate(`${routes.issbAnswerDetail}?id=${usageItem.questionId}`);
  };

  if (!row) return null;

  const groupLabel = row.category ? row.category.charAt(0) + row.category.slice(1).toLowerCase() : "";
  const displayValue =
    row.dataValue == null || row.dataValue === ""
      ? "Not reported"
      : row.valueNumeric != null
      ? getFormattedNumValue(row.valueNumeric, 2)
      : row.dataValue;
  const entries = metadataEntries(row.metadata);
  // A review row is one document's extracted reading — no datapointUid until it is accepted.
  const isReviewRow = !row.datapointUid;
  const history = isReviewRow ? { how: "", status: null, version: null } : figureHistory(row);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay bg="rgba(5,31,35,.55)" />
      <ModalContent maxW="960px" borderRadius="16px">
        <Box px={7} pt={6} pb={5} borderBottom="1px solid" borderColor="gray.200">
          {groupLabel ? (
            <Text fontSize="11px" fontWeight="800" letterSpacing="0.08em" color="gray.500" textTransform="uppercase">
              {groupLabel}
            </Text>
          ) : null}
          <Text fontSize="22px" fontWeight="800" color="darkTeal" letterSpacing="-0.4px">
            {row.dataKey}
          </Text>
          <HStack align="baseline" spacing={2} mt={2}>
            <Text fontSize="24px" fontWeight="800" color="darkTeal" sx={{ fontVariantNumeric: "tabular-nums" }}>
              {displayValue}
            </Text>
            {row.unit ? (
              <Text fontSize="13px" fontWeight="600" color="gray.600">
                {row.unit}
              </Text>
            ) : null}
            {usage?.currentVersion != null ? (
              <Badge ml={1} variant="subtle" bg="gray.100" color="gray.600">
                Version {usage.currentVersion}
              </Badge>
            ) : null}
            {history.status ? <StatusTag status={history.status} /> : null}
          </HStack>
          {/* Review overlay: this is one document's reading, not yet in Foundation Data. */}
          {isReviewRow ? (
            <Text fontSize="12.5px" color="gray.500" mt={2}>
              Found in this document · not yet added to Foundation Data
            </Text>
          ) : null}
        </Box>
        <ModalCloseButton top={5} right={5} />

        <ModalBody p={0}>
          <Flex minH="0">
            {/* Left column — About this figure */}
            <Box w="340px" flexShrink={0} p={6} bg="gray.50" borderRight="1px solid" borderColor="gray.200">
              <Text fontSize="13px" fontWeight="700" color="darkTeal" mb={4}>
                About this figure
              </Text>
              <Flex direction="column" gap={5}>
                {/* Which workstream the figure belongs to. A figure has no entity or year of its own — it is filed
                    under one (entity, workstream, reporting year), and since ESG-4018 every figure in it is for that
                    year's reporting period — so this reads off the workstream rather than off the row. */}
                {dataYear ? (
                  <HStack spacing={6} align="start">
                    <Box>
                      <Text fontSize="11.5px" fontWeight="600" color="gray.500">
                        Entity
                      </Text>
                      <Text fontSize="13.5px" color="darkTeal">
                        {dataYear.entityName || "—"}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="11.5px" fontWeight="600" color="gray.500">
                        Reporting year
                      </Text>
                      <Text fontSize="13.5px" color="darkTeal">
                        {dataYear.year}
                      </Text>
                      {dataYear.filtersByPeriod && dataYear.periodStart ? (
                        <Text fontSize="12px" color="gray.500">
                          {formatPeriod({ start: dataYear.periodStart, end: dataYear.periodEnd })}
                        </Text>
                      ) : null}
                    </Box>
                  </HStack>
                ) : null}
                {!isReviewRow ? (
                  <Box>
                    <Text fontSize="11.5px" fontWeight="600" color="gray.500">
                      History
                    </Text>
                    <Text fontSize="13.5px" color="darkTeal">
                      {history.how}
                      {history.version != null ? ` · version ${history.version}` : ""}
                    </Text>
                  </Box>
                ) : null}
                <Box>
                  <Text fontSize="11.5px" fontWeight="600" color="gray.500">
                    Applies to
                  </Text>
                  <Text fontSize="13.5px" color="darkTeal">
                    {row.scope || "Whole organisation"}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="11.5px" fontWeight="600" color="gray.500">
                    Source
                  </Text>
                  <Text fontSize="13px" color="darkTeal" wordBreak="break-all">
                    {sourceLabel(row)}
                  </Text>
                </Box>
                {row.sourceQuote ? (
                  <Box>
                    <Text fontSize="11.5px" fontWeight="600" color="gray.500" mb={1}>
                      Extracted from
                    </Text>
                    <Text fontSize="13px" lineHeight="1.55" color="gray.700" fontStyle="italic" pl={3} borderLeft="2px solid" borderColor="gray.300">
                      “{row.sourceQuote}”
                    </Text>
                  </Box>
                ) : null}
                {entries.length > 0 ? (
                  <Box>
                    <Text fontSize="11.5px" fontWeight="600" color="gray.500" mb={1}>
                      Details
                    </Text>
                    <Flex wrap="wrap" gap={1}>
                      {entries.map((entry) => (
                        <Badge key={entry.key} variant="subtle" colorScheme="gray" fontWeight="400" textTransform="none">
                          {entry.text}
                        </Badge>
                      ))}
                    </Flex>
                  </Box>
                ) : null}
                <HStack spacing={4}>
                  <Box>
                    <Text fontSize="11.5px" fontWeight="600" color="gray.500" mb={1}>
                      Extraction confidence
                    </Text>
                    {row.origin === "USER" ? (
                      <Badge colorScheme="purple">manual</Badge>
                    ) : row.confidence ? (
                      <Badge colorScheme={CONFIDENCE_COLORS[row.confidence] || "gray"}>{row.confidence}</Badge>
                    ) : (
                      <Text fontSize="13.5px" color="gray.500">
                        —
                      </Text>
                    )}
                  </Box>
                </HStack>
              </Flex>
            </Box>

            {/* Right column — Where it's used */}
            <Box flex="1" minW={0} p={6}>
              <Flex align="baseline" justify="space-between" gap={3} mb={4}>
                <Text fontSize="13px" fontWeight="700" color="darkTeal">
                  Where it&apos;s used
                </Text>
                {!isLoading && questions.length > 0 ? (
                  <Text fontSize="12.5px" color="gray.500">
                    <Text as="span" fontWeight="700" color="darkTeal">
                      {questions.length}
                    </Text>{" "}
                    answer{questions.length === 1 ? "" : "s"} across{" "}
                    <Text as="span" fontWeight="700" color="darkTeal">
                      {roundGroups.length}
                    </Text>{" "}
                    round{roundGroups.length === 1 ? "" : "s"}
                  </Text>
                ) : null}
              </Flex>

              {isLoading ? (
                <Spinner size="sm" />
              ) : questions.length === 0 ? (
                <Text fontSize="sm" color="gray.500">
                  No report answers have been drafted from this figure yet. Answers record which figures
                  they used when an AI draft is applied, so this fills in as the report is written.
                </Text>
              ) : (
                <Flex direction="column" gap={5}>
                  {roundGroups.map((group) => {
                    const roundState = group.round?.state;
                    const isCurrent = group.round && String(group.round.id) === String(currentRoundId);
                    const tag = isCurrent
                      ? { label: "Current round", color: "#85C22A", bg: "rgba(133,194,42,.18)" }
                      : ROUND_STATUS_TAG[roundState];
                    return (
                      <Flex key={group.roundId} direction="column" gap={2}>
                        <Flex align="center" gap={3} bg="darkTeal" borderRadius="12px" px={4} py={3}>
                          <RoundIcon />
                          <Flex direction="column" gap={0}>
                            <Text fontSize="10.5px" fontWeight="800" letterSpacing="0.08em" color="#B5E06C">
                              ROUND
                            </Text>
                            <Text fontSize="13.5px" fontWeight="700" color="white">
                              {group.round?.name || "Unknown round"}
                            </Text>
                            <Text fontSize="12px" color="#A4B4B8">
                              {group.items.length} requirement{group.items.length === 1 ? "" : "s"} in this round using this figure
                            </Text>
                          </Flex>
                          {tag ? (
                            <Badge
                              ml="auto"
                              fontSize="10.5px"
                              fontWeight="800"
                              letterSpacing="0.04em"
                              color={tag.color}
                              bg={tag.bg}
                              px={2}
                              borderRadius="4px"
                            >
                              {tag.label}
                            </Badge>
                          ) : null}
                        </Flex>
                        {group.items.map((item) => {
                          const key = `${item.questionId}-${item.usedVersion}`;
                          const expanded = openQuestionKey === key;
                          const toggle = () => {
                            if (expanded) {
                              setOpenQuestionKey(null);
                              setOpenQuestionId(null);
                            } else {
                              setOpenQuestionKey(key);
                              setOpenQuestionId(item.questionId);
                            }
                          };
                          return (
                            <Box key={key} bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="12px" p={4}>
                              <Flex
                                align="flex-start"
                                justify="space-between"
                                gap={3}
                                cursor="pointer"
                                onClick={toggle}
                              >
                                <Box minW={0}>
                                  <HStack spacing={2} mb={1} flexWrap="wrap">
                                    {item.issbReference ? (
                                      <Text fontSize="11px" color="gray.400">
                                        {item.issbReference}
                                      </Text>
                                    ) : null}
                                    <HStack spacing={1}>
                                      <Box
                                        w="6px"
                                        h="6px"
                                        borderRadius="50%"
                                        bg={item.superseded ? "orange.500" : "limeShade.600"}
                                      />
                                      <Text fontSize="11px" fontWeight="600" color={item.superseded ? "orange.600" : "limeShade.600"}>
                                        {item.superseded
                                          ? `Uses v${item.usedVersion ?? "?"} — outdated`
                                          : `Up to date (v${item.usedVersion})`}
                                      </Text>
                                    </HStack>
                                  </HStack>
                                  <Text fontSize="14px" fontWeight="700" lineHeight="1.4" color="darkTeal">
                                    {item.question}
                                  </Text>
                                </Box>
                                <Flex
                                  w="28px"
                                  h="28px"
                                  flexShrink={0}
                                  borderRadius="50%"
                                  bg="teal"
                                  align="center"
                                  justify="center"
                                >
                                  <CaretDown
                                    size={12}
                                    color="white"
                                    style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}
                                  />
                                </Flex>
                              </Flex>
                              {expanded ? (
                                <Box mt={3} pt={3} borderTop="1px solid" borderColor="gray.200">
                                  {/*
                                    The actual answer — Owner/Assignees/Stage, the rich-text answer with its
                                    own clickable inline citations, and the status pills — same as the
                                    Requirements page's own expanded QuestionCard, reusing its QCardBody
                                    rather than re-building the layout here. `readOnly` so this read-only
                                    usage view cannot change the real question's assignees/status; `Card`
                                    wraps it unstyled because QCardBody renders CardBody, which throws
                                    outside a Card's styles context.
                                  */}
                                  {isLoadingQuestion ? (
                                    <Spinner size="sm" />
                                  ) : questionData && questionData.id === item.questionId ? (
                                    <Card variant="unstyled" bg="transparent" sx={ANSWER_PREVIEW_TEXT}>
                                      <QCardBody q={questionData} readOnly />
                                    </Card>
                                  ) : (
                                    <Text fontSize="sm" color="gray.500">
                                      Could not load this answer.
                                    </Text>
                                  )}
                                  <Flex justify="flex-end" mt={2}>
                                    {item.roundId && roundsById[item.roundId] ? (
                                      <Button variant="link" size="xs" colorScheme="teal" onClick={() => openAnswer(item)}>
                                        Open answer →
                                      </Button>
                                    ) : null}
                                  </Flex>
                                </Box>
                              ) : null}
                            </Box>
                          );
                        })}
                      </Flex>
                    );
                  })}
                </Flex>
              )}
            </Box>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

/** Approved vs pending, in the same colours the table uses (orange = not approved yet). */
function StatusTag({ status }) {
  const pending = status === "Pending approval";
  return (
    <Badge
      ml={1}
      variant="subtle"
      colorScheme={pending ? "orange" : "green"}
      textTransform="none"
      fontSize="12px"
      px={2}
    >
      {status}
    </Badge>
  );
}

/*
  The answer text inside a preview, scaled down to the overlay's own type size. The shared RichTextViewer
  renders at page size (it is the Requirements page's answer), which towers over everything else in an
  overlay — so it is overridden here only, never in the shared editor CSS.
*/
const ANSWER_PREVIEW_TEXT = {
  ".tiptap": { fontSize: "14px", lineHeight: 1.55 },
  ".tiptap p, .tiptap li": { fontSize: "14px" },
  ".tiptap h1": { fontSize: "18px" },
  ".tiptap h2": { fontSize: "16px" },
  ".tiptap h3": { fontSize: "15px" },
};
